import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api';
import './UserDetail.css';
import backIcon from '../../assets/backspace-icon.png';

function UserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [postReportCount, setPostReportCount] = useState(0);
  const [productReportCount, setProductReportCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const res = await api.get(`/admin/users/${id}`);
      setUser(res.data);

      const reportsRes = await api.get('/reports/admin');
      const allReports = reportsRes.data.reports || [];

      const userReports = allReports.filter(
        (r) => r.reportedUsername === res.data.username
      );

      setPostReportCount(userReports.filter((r) => r.reportedType === 'post').length);
      setProductReportCount(userReports.filter((r) => r.reportedType === 'product').length);
    } catch (err) {
      console.error('Failed to fetch user details', err);
    }
  };

  const handleSuspendToggle = async () => {
    if (!user) return;

    if (user.userType === 'merchant') {
      alert('Please use the merchant admin panel for merchant accounts.');
      return;
    }

    try {
      setLoading(true);
      const res = await api.put(`/admin/users/${id}/toggle-suspension`);
      const updatedStatus = res.data.status;
      setUser({ ...user, status: updatedStatus });

      alert(updatedStatus === 'suspended'
        ? 'User has been suspended.'
        : 'User has been reinstated.');
    } catch (err) {
      console.error('❌ Failed to toggle user suspension:', err);
      alert('Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this user? This action is permanent.')) return;
    try {
      setLoading(true);
      await api.delete(`/admin/users/${id}`);
      navigate('/admin/users');
    } catch (err) {
      console.error('Failed to delete user', err);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return <div className="user-detail-loading">Loading...</div>;

  const handleTag = user.userType === 'merchant' && user.businessUsername
    ? user.businessUsername
    : user.username;

  return (
    <div className="user-detail-container">
      <div className="user-detail-topbar">
        <img
          src={backIcon}
          alt="Back"
          className="back-arrow"
          onClick={() => navigate(-1)}
        />
        <span className="user-detail-title">User Detail</span>
      </div>

      <div className="user-detail-scroll-area">
        <div className="user-detail-content">
          <div className="user-profile-section">
            <img
              src={user.profilePic || '/placeholder-profile.png'}
              alt="Profile"
              className="user-profile-pic"
            />
            <div className="user-name-username">
              <h2>{user.name}</h2>
              <p>@{handleTag}</p>
            </div>
          </div>

          <div className="user-info-columns">
            <div className="user-info-column">
              <div><strong>Role:</strong> {user.userType}</div>
              <div className="user-email"><strong>Email:</strong> {user.email}</div>
              <div><strong>Joined:</strong> {new Date(user.createdAt).toLocaleDateString()}</div>
              {user.phone && <div><strong>Phone:</strong> {user.phone}</div>}
            </div>

            {user.userType === 'merchant' && (
              <div className="user-info-column">
                <div><strong>Store Name:</strong> {user.storeName || '—'}</div>
                <div><strong>Description:</strong> {user.storeDescription || '—'}</div>
                <div><strong>Listings:</strong> {user.totalListings || 0}</div>

                {user.storeLocation && (
                  <div><strong>Store Location:</strong> {user.storeLocation}</div>
                )}
                {user.storeCreatedAt && (
                  <div><strong>Application Date:</strong> {new Date(user.storeCreatedAt).toLocaleDateString()}</div>
                )}

                <div>
                  <strong>Merchant Status:</strong>{' '}
                  {user.status ? (
                    <span className={`status-label ${user.status}`}>
                      {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                    </span>
                  ) : (
                    <em>Not Set</em>
                  )}
                </div>

                {user.businessLogo && (
                  <div><strong>Business Logo:</strong> <img src={user.businessLogo} className="thumb" /></div>
                )}
                {user.bannerImage && (
                  <div><strong>Banner Image:</strong> <img src={user.bannerImage} className="thumb" /></div>
                )}
              </div>
            )}
          </div>

          <div className="user-activity-section">
            <h3>Platform Activity</h3>
            <div><strong>Posts:</strong> {user.totalPosts || 0}</div>
            <div><strong>Tips Earned:</strong> {user.totalTips || 0}π</div>
            <div><strong>Followers:</strong> {user.followers?.length || 0}</div>
            <div><strong>Following:</strong> {user.following?.length || 0}</div>

            <div className="userdetail-reports">
              <strong>Post Reports:</strong> {postReportCount}<br />
              <strong>Product Reports:</strong> {productReportCount}
            </div>
          </div>

          <div className="user-admin-buttons">
            <button
              className={`suspend-btn ${user.status === 'suspended' ? 'reinstate' : ''}`}
              onClick={handleSuspendToggle}
              disabled={loading}
            >
              {user.status === 'suspended' ? 'Reinstate User' : 'Suspend User'}
            </button>
            <button className="delete-btn" onClick={handleDelete} disabled={loading}>
              Delete User
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserDetail;
